import { ChangeEvent, forwardRef, InputHTMLAttributes, useImperativeHandle, useRef, useState, FocusEvent } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "./badge";
import { X } from "lucide-react";

export interface InputWithBadgesProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value?: string[];
  onChange?: (value: string[]) => void;
  badgeVariant?: "default" | "secondary" | "destructive" | "outline";
  placeholder?: string;
  clearButtonText?: string;
  validateTagInput?: (tag: string, currentTags: string[]) => boolean;
}

const InputWithBadges = forwardRef<HTMLInputElement, InputWithBadgesProps>(
  (
    {
      className,
      badgeVariant = "default",
      value = [],
      onChange,
      placeholder,
      clearButtonText,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>("");
    const innerRef = useRef<HTMLInputElement>(null);

    // Merge refs
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const handleAddBadge = () => {
      const trimmedInputValue = inputValue.trim();

      if (trimmedInputValue) {
        onChange?.([...value, trimmedInputValue]);
        setInputValue("");
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddBadge();
      } else if (
        e.key === "Backspace" &&
        inputValue === "" &&
        value.length > 0
      ) {
        // Remove the last badge when pressing backspace on empty input
        onChange?.(value.slice(0, -1));
      }
    };

    const handleRemoveBadge = (index: number) => {
      const newBadges = value.filter((_, i) => i !== index);
      onChange?.(newBadges);
    };

    const handleClearAllBadges = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.([]);
      innerRef.current?.focus();
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      handleAddBadge();

      if (props.onBlur) {
        props.onBlur(e);
      }
    };

    const showClearButton = value.length > 0 && clearButtonText;

    return (
      <div
        className={cn(
          "flex h-auto min-h-9 w-full flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-1 shadow-sm focus-within:ring-1 focus-within:ring-ring relative",
          className
        )}
        onClick={() => innerRef.current?.focus()}
      >
        {value.map((badge, index) => (
          <Badge
            key={`${badge}-${index}`}
            variant={badgeVariant}
            className="flex items-center gap-1 max-w-[50%] px-2"
          >
            <span className="truncate">{badge}</span>
            <X
              className="h-3 w-3 cursor-pointer hover:text-muted-foreground/80 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveBadge(index);
              }}
            />
          </Badge>
        ))}
        <input
          ref={innerRef}
          className="flex-1 min-w-[120px] border-none bg-transparent p-1 text-sm focus:outline-none focus:ring-0"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          {...props}
          onBlur={handleBlur}
        />
        {showClearButton && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
            onClick={handleClearAllBadges}
          >
            {clearButtonText}
          </button>
        )}
      </div>
    );
  }
);

InputWithBadges.displayName = "InputWithBadges";

export { InputWithBadges }; 